import React from 'react'
import { Row, Form, FormLabel, Col, Button, FormGroup, Modal } from 'react-bootstrap'
import Moment from 'moment'

// definitions
import { AccountXeroOrg, Calendar as CalendarData, Employee, LeaveApplication, LeaveType, Recurrence } from '../../../../../back-end/utilities/apiDefinitions'
import * as AppDefinitions from '../../../App.d'

// components
import { CalendarLeave } from '../../ui/bigCalendar/bigCalendar'

// utillites
import * as Request from '../../../utilities/request'
import * as I from '../../../utilities/me'
import Select from 'react-select'
import * as yup from 'yup'
import { MessageAction } from '../../ui/messages/message'
import { formatLeaveTitle, getAmount } from '../../ui/bigCalendar/utilities'
import { HandleEventPopUp } from './handleEventPopup'
import moment from 'moment'

type EndingRecurrences = 'Never' | 'On' | 'After'

interface CalendarEntryPopUpProps {
	handleClose: () => void
	titleText: string
	types: LeaveType[] | null
	employees: Employee[] | undefined
	leaveApplications: CalendarLeave[]
	updateLeaveApplications: React.Dispatch<React.SetStateAction<LeaveApplication[]>>
	updateMessages: (message: MessageAction) => void
	selectedDateForEvent?: string | null
	show: boolean
	appState: AppDefinitions.AppState
	currentOrg: AccountXeroOrg
	currentCalendar: CalendarData
	xeroOrgCalendars: CalendarData[]
	leaveApplicationID?: string | null
	showCalendar?: boolean
}

const leaveApp: LeaveApplication & { updatedStartOrEnd: boolean } = {
	description: '',
	startDate: undefined,
	endDate: undefined,
	employeeID: '',
	xeroOrgLeaveTypeID: '',
	leavePeriod: [
		{
			startDate: '',
			endDate: '',
			numberOfUnits: undefined,
		},
	],
	leaveType: undefined,
	employee: undefined,
	updatedStartOrEnd: true,
	isNonXero: true,
}

const recurrenceEntry: Recurrence = {
	recurrenceID: '',
	weeklyRegularity: undefined,
	startDate: undefined,
	endDate: null,
	isMonday: false,
	isTuesday: false,
	isWednesday: false,
	isThursday: false,
	isFriday: false,
	isSaturday: false,
	isSunday: false,
	typeIsWeekly: true,
	typeIsMonthly: false,
	typeIsAnnually: false,
}

const recurrenceRegularity = [
	{ label: 'week(s)', value: 'week' },
	{ label: 'month(s)', value: 'month' },
	{ label: 'year(s)', value: 'year' },
]

export const CalendarEntryPopUp = (props: CalendarEntryPopUpProps) => {
	const [isEdit, updateIsEdit] = React.useState<boolean>(false)
	const [leaveApplication, updateLeaveApplication] = React.useState<LeaveApplication & { updatedStartOrEnd: boolean }>(leaveApp)
	const [recurring, updateRecurring] = React.useState<string>('No')
	const [recurrenceLeave, updateRecurrenceLeave] = React.useState<Recurrence>(recurrenceEntry)
	const [endingRecurrence, updateEndingRecurrence] = React.useState<EndingRecurrences>('Never')
	const [occurrences, updateOccurrences] = React.useState<number | null>(null)
	const [showEditRecurrenceDialog, updateShowEditRecurrenceDialog] = React.useState<boolean>(false)
	const [showDialog, updateShowDialog] = React.useState<boolean>(props.show)
	const [loading, updateLoading] = React.useState<boolean>(false)

	React.useEffect(
		() => {
			const fetchData = async () => {
				const [leaveApplicationReq] = await Promise.all([
					Request.get(`leave?where=LeaveApplicationID=in(${props.leaveApplicationID})`, props.appState.authState),
				])
				const leaveApplication = leaveApplicationReq.data.leaveApplications[0]
				const recurrence = leaveApplicationReq.data.leaveApplications[0].recurrence!
				const startDate = leaveApplication.startDate ? leaveApplication.startDate : null
				const endDate = leaveApplication.endDate
					? props.currentOrg.xeroOrg && props.currentOrg.xeroOrg.hoursInDay! > leaveApplication.leavePeriod[0].numberOfUnits
						? moment(leaveApplication.startDate)
								.add(leaveApplication.leavePeriod![0].numberOfUnits! * 60, 'minutes')
								.toISOString()
						: leaveApplication.endDate
					: null
				updateLeaveApplication({
					...leaveApplication,
					startDate: startDate,
					endDate: endDate,
					updatedStartOrEnd: false,
					leavePeriod: leaveApplication.isNonXero
						? [
								{
									...leaveApplication.leavePeriod![0],
									numberOfUnits: leaveApplication.leavePeriod![0].numberOfUnits || calculateNumberOfHours(startDate, endDate),
								},
						  ]
						: leaveApplication.leavePeriod!,
				})
				updateIsEdit(true)

				if (recurrence.recurrenceID !== null) {
					updateRecurring('Yes')
					updateRecurrenceLeave({
						...recurrence,
						startDate: recurrence.startDate,
						endDate: recurrence.endDate ? recurrence.endDate : null,
					})
					updateEndingRecurrence(recurrence.endDate ? 'On' : 'Never')
				}
			}

			if (props.appState.authState && props.leaveApplicationID) {
				fetchData()
			}

			if (props.appState.authState && props.selectedDateForEvent) {
				updateLeaveApplication({
					...leaveApplication,
					startDate: props.selectedDateForEvent,
					endDate: props.selectedDateForEvent,
					leavePeriod: [
						{
							...leaveApplication.leavePeriod![0],
							numberOfUnits: calculateNumberOfHours(props.selectedDateForEvent, props.selectedDateForEvent),
						},
					],
				})
			}
		},
		// eslint-disable-next-line
		[props.appState.authState, props.leaveApplicationID, props.selectedDateForEvent]
	)

	// ---- Validation Schemas ---- //
	const schema = React.useMemo(() => getSchema(endingRecurrence), [endingRecurrence])

	const resetModal = React.useCallback(() => {
		updateLeaveApplication({ ...leaveApp })
		updateRecurring('No')
		updateRecurrenceLeave(recurrenceEntry)
		updateEndingRecurrence('Never')
		updateOccurrences(null)
	}, [updateLeaveApplication, updateRecurring, updateRecurrenceLeave, updateEndingRecurrence, updateOccurrences])

	const handleUpdateNonRecurringEvent = React.useCallback(
		async (updatedLeaveApp: LeaveApplication) => {
			updateLoading(true)
			try {
				const employeeID = updatedLeaveApp.employeeID
				const affectedCals = props.xeroOrgCalendars!.filter((c) => c.employees!.find((emp) => emp.employeeID === employeeID))
				const [req] = await Promise.all([
					Request.put(`leave/${props.leaveApplicationID}`, updatedLeaveApp, props.appState.authState),
					...affectedCals.map(async (c) =>
						Request.post(`organisation/${c.accountXeroOrg!.xeroOrgID}/updatecalendar`, null, props.appState.authState)
					),
				])

				if (req.data.success) {
					props.updateMessages({
						type: 'add',
						data: {
							severity: 'success',
							message: 'Successfully updated custom leave',
							timeout: 3000,
							dismissible: true,
						},
					})
					resetModal()
					props.handleClose()
					const newLeave = req.data.leaveApplications[0]
					props.updateLeaveApplications(props.leaveApplications.concat(newLeave))
					updateShowDialog(false)
					updateLoading(false)
				}
			} catch (err) {
				props.updateMessages({
					type: 'add',
					data: {
						severity: 'danger',
						message: `Unable to update event`,
						timeout: 3000,
						dismissible: true,
					},
				})
			}
		},
		[
			props.xeroOrgCalendars,
			props.leaveApplicationID,
			props.appState.authState,
			props.currentCalendar,
			props.currentOrg,
			props.leaveApplications,
			props.updateLeaveApplications,
			props.updateMessages,
			props.handleClose,
		]
	)

	const handleUpdatePress = React.useCallback(async () => {
		if (
			I.have('admin', props.appState.permission[props.appState.context]) ||
			props.appState.authState.attributes!['custom:userId'] === leaveApplication.createUserID
		) {
			updateLoading(true)
			const updatedLA = {
				...leaveApplication,
				leavePeriod: [
					{
						...leaveApplication.leavePeriod![0],
						startDate: leaveApplication.startDate,
						endDate: leaveApplication.endDate,
						status: 0,
					},
				],
				recurrence:
					recurring === 'Yes'
						? {
								weeklyRegularity: recurrenceLeave.weeklyRegularity,
								startDate: recurrenceLeave.startDate,
								endDate: recurrenceLeave.endDate,
								isMonday: recurrenceLeave.isMonday,
								isTuesday: recurrenceLeave.isTuesday,
								isWednesday: recurrenceLeave.isWednesday,
								isThursday: recurrenceLeave.isThursday,
								isFriday: recurrenceLeave.isFriday,
								isSaturday: recurrenceLeave.isSaturday,
								isSunday: recurrenceLeave.isSunday,
						  }
						: undefined,
			}

			try {
				await schema.validate(updatedLA, { abortEarly: false })
				if (recurring === 'Yes') {
					updateShowEditRecurrenceDialog(true)
					updateShowDialog(false)
				} else {
					handleUpdateNonRecurringEvent(updatedLA)
				}
			} catch (validationError) {
				if (validationError instanceof yup.ValidationError) {
					const newErrors = validationError.inner.map((err) => err.errors.join('\n'))
					props.updateMessages({
						type: 'add',
						data: {
							severity: 'danger',
							message: `Unable to update Custom calendar entry: \n\n ${newErrors
								.filter((val: string, id: number, array: string | string[]) => array.indexOf(val) === id)
								.join('\n')}`,
							timeout: 3000,
							dismissible: true,
						},
					})
				}
				return
			}
			updateLoading(false)
		} else {
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'danger',
					message: `You don't have permissions to update this Custom calendar entry`,
					timeout: 3000,
					dismissible: true,
				},
			})
		}
	}, [props.updateMessages, leaveApplication])

	const handleCalendarEntryCreation = React.useCallback(async () => {
		updateLoading(true)
		try {
			const newLA = {
				...leaveApplication,
				isNonXero: true,
				updatedDateUTC: Moment.utc(new Date()).format('YYYY-MM-DD'),
				xeroOrgLeaveTypeID: leaveApplication.leaveType ? leaveApplication.leaveType!.xeroOrgLeaveTypeID : '',
				leavePeriod: [
					{
						...leaveApplication.leavePeriod![0],
						startDate: leaveApplication.startDate,
						endDate: leaveApplication.endDate,
						status: 0,
					},
				],
				recurrence:
					recurring === 'Yes'
						? {
								weeklyRegularity: recurrenceLeave.weeklyRegularity,
								startDate: recurrenceLeave.startDate,
								endDate: recurrenceLeave.endDate,
								isMonday: recurrenceLeave.isMonday,
								isTuesday: recurrenceLeave.isTuesday,
								isWednesday: recurrenceLeave.isWednesday,
								isThursday: recurrenceLeave.isThursday,
								isFriday: recurrenceLeave.isFriday,
								isSaturday: recurrenceLeave.isSaturday,
								isSunday: recurrenceLeave.isSunday,
								typeIsWeekly: recurrenceLeave.typeIsWeekly,
								typeIsMonthly: recurrenceLeave.typeIsMonthly,
								typeIsAnnually: recurrenceLeave.typeIsAnnually,
						  }
						: undefined,
			}

			try {
				await schema.validate(newLA, { abortEarly: false })
			} catch (validationError) {
				if (validationError instanceof yup.ValidationError) {
					const newErrors = validationError.inner.map((err) => err.errors.join('\n'))
					props.updateMessages({
						type: 'add',
						data: {
							severity: 'danger',
							message: `Unable to add Custom calendar entry: \n\n ${newErrors
								.filter((val: string, id: number, array: string | string[]) => array.indexOf(val) === id)
								.join('\n')}`,
							timeout: 3000,
							dismissible: true,
						},
					})
				}
				return
			}

			const employeeID = newLA.employeeID
			const affectedCals = props.xeroOrgCalendars!.filter((c) => c.employees!.find((emp) => emp.employeeID === employeeID))
			const [req] = await Promise.all([
				Request.post(`leave`, newLA, props.appState.authState),
				...affectedCals.map(async (c) => Request.post(`organisation/${c.accountXeroOrg!.xeroOrgID}/updatecalendar`, null, props.appState.authState)),
			])
			if (req.data.success) {
				props.updateMessages({
					type: 'add',
					data: {
						severity: 'success',
						message: 'Successfully created custom leave',
						timeout: 3000,
						dismissible: true,
					},
				})
				const newLeave = req.data.leaveApplications[0]
				const event = {
					start: newLeave.startDate!,
					end: newLeave.endDate!,
					description: newLeave.description!,
					isNonXero: newLeave.leaveType.isNonXero,
					leaveApplicationID: newLeave.leaveApplicationID,
					title: `${
						(props.currentCalendar.showLeaveType && newLeave.leaveType) ||
						(newLeave.leaveType && newLeave.leaveType.isNonXero && newLeave.leaveType.showTypeInCalendar)
							? newLeave.leaveType.name
							: 'Leave'
					}: ${newLeave.employee!.alias ? newLeave.employee!.alias : `${newLeave.employee!.firstName} ${newLeave.employee!.lastName}`}${
						props.currentCalendar.showTitle ? formatLeaveTitle(newLeave.employee!.jobTitle!, '-') : ''
					}${props.currentCalendar.showGroup ? formatLeaveTitle(newLeave.employee!.groupName!, '-') : ''}${
						props.currentCalendar.showAmount && props.currentOrg.xeroOrg
							? getAmount(newLeave, props.currentOrg.xeroOrg.showLeaveInDays!, props.currentOrg.xeroOrg.hoursInDay!)
							: ''
					}`,
				}
				props.updateLeaveApplications(props.leaveApplications.concat(event))
				resetModal()
				props.handleClose()
			}
		} catch (err) {
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'danger',
					message: 'Unable to create custom leave',
					timeout: 3000,
					dismissible: true,
				},
			})
		}
		updateLoading(false)
	}, [
		leaveApplication,
		recurring,
		recurrenceLeave,
		props.xeroOrgCalendars,
		props.appState.authState,
		props.currentCalendar,
		props.currentOrg,
		props.updateLeaveApplications,
		props.leaveApplications,
		props.handleClose,
		props.updateMessages,
	])

	const calculateNumberOfHours = React.useCallback(
		(startDate: string | undefined | null, endDate: string | undefined | null) => {
			if (startDate && endDate && props.currentOrg.xeroOrg) {
				const start = moment(startDate)
				const end = moment(endDate)

				// Check if both times are exactly same
				const timeStart = start.format('HH:mm:ss')
				const timeEnd = end.format('HH:mm:ss')
				if (timeStart === timeEnd) {
					// Calculate the difference in days
					const days = Math.abs(end.diff(start, 'days')) + 1
					return days * props.currentOrg.xeroOrg.hoursInDay!
				} else {
					// Calculate the difference in hours
					const hours = Math.abs(end.diff(start, 'hours'))
					return hours
				}
			}

			return 0
		},
		[props.currentOrg]
	)

	return (
		<>
			<Modal
				show={showDialog}
				aria-labelledby="modal"
				centered
				onHide={() => {
					resetModal()
					props.handleClose()
				}}
			>
				{props.titleText ? (
					<Modal.Header>
						<Modal.Title>{props.titleText}</Modal.Title>
					</Modal.Header>
				) : null}
				<Modal.Body>
					<FormGroup>
						<Row>
							<Col sm="6">
								<Form.Control
									type="date"
									name="startDate"
									disabled={!leaveApplication.isNonXero}
									value={leaveApplication.startDate ? moment.utc(leaveApplication.startDate).local().format('YYYY-MM-DD') : ''}
									max={Moment.utc().add('1', 'year').format('YYYY-MM-DD')}
									onChange={(e) => {
										const localDate = moment.utc(e.target.value, 'YYYY-MM-DD').startOf('day')
										const utcDate = localDate.toISOString()
										if (recurring === 'Yes') {
											updateRecurrenceLeave({
												...recurrenceLeave,
												startDate: utcDate,
												endDate: utcDate,
											})
										}
										const todayInOneYear = Moment.utc().add('1', 'year')
										const threeMonthsBefore = Moment.utc().subtract('90', 'days')
										if (Moment.utc(utcDate).isSameOrAfter(threeMonthsBefore) && Moment.utc(utcDate).isSameOrBefore(todayInOneYear)) {
											updateLeaveApplication({
												...leaveApplication,
												startDate: utcDate,
												endDate: utcDate,
												updatedStartOrEnd: true,
												leavePeriod: [
													{
														...leaveApplication.leavePeriod![0],
														numberOfUnits: calculateNumberOfHours(utcDate, utcDate),
													},
												],
											})
										}
									}}
								/>
							</Col>
							<Col sm="6">
								<Form.Control
									type="date"
									name="endDate"
									disabled={!leaveApplication.isNonXero}
									value={leaveApplication.endDate ? moment.utc(leaveApplication.endDate).local().format('YYYY-MM-DD') : ''}
									onChange={(e) => {
										const localDate = moment.utc(e.target.value, 'YYYY-MM-DD').startOf('day')
										const utcDate = localDate.toISOString()
										updateLeaveApplication({
											...leaveApplication,
											endDate: utcDate,
											updatedStartOrEnd: true,
											leavePeriod: [
												{
													...leaveApplication.leavePeriod![0],
													numberOfUnits: calculateNumberOfHours(leaveApplication.startDate, utcDate),
												},
											],
										})
									}}
								/>
							</Col>
						</Row>
						<Row style={{ padding: '15px' }}>
							<Form.Control
								type="number"
								placeholder={'Number of hours'}
								value={leaveApplication.leavePeriod![0].numberOfUnits}
								disabled={!leaveApplication.isNonXero}
								min={0}
								onChange={(e) => {
									const inputHours = Number(e.target.value)
									const hoursInDay = Number(props.currentOrg.xeroOrg!.hoursInDay!)
									const originalStart = moment.utc(leaveApplication.startDate) // input in UTC

									let startDate: string
									let endDate: string

									if (inputHours >= hoursInDay) {
										// Convert to browser local time
										const localDate = originalStart.clone().local()

										// Get local calendar date components
										const year = localDate.year()
										const month = localDate.month()
										const date = localDate.date()

										// Construct a new moment in UTC at that local calendar date
										const normalizedUtcStart = moment.utc({ year, month, day: date })

										startDate = normalizedUtcStart.toISOString()

										const daysToAdd = Math.floor(inputHours / hoursInDay)

										endDate =
											inputHours === hoursInDay
												? normalizedUtcStart.clone().toISOString()
												: normalizedUtcStart.clone().add(daysToAdd, 'days').toISOString()
									} else {
										startDate = originalStart.toISOString()
										endDate = originalStart
											.clone()
											.add(inputHours * 60, 'minutes')
											.toISOString()
									}

									updateLeaveApplication({
										...leaveApplication,
										startDate: startDate,
										endDate: endDate,
										leavePeriod: [
											{
												...leaveApplication.leavePeriod![0],
												startDate: startDate,
												endDate: endDate,
												numberOfUnits: Number(e.target.value),
											},
										],
									})
								}}
							/>
						</Row>
						{props.currentOrg.xeroOrg &&
						leaveApplication.leavePeriod &&
						leaveApplication.leavePeriod[0] &&
						leaveApplication.leavePeriod[0].numberOfUnits &&
						props.currentOrg.xeroOrg.hoursInDay! > leaveApplication.leavePeriod[0].numberOfUnits ? (
							<Row style={{ padding: '15px', flexWrap: 'nowrap' }}>
								<p style={{ padding: '7px' }}> From </p>
								<Form.Control
									type="time"
									value={moment(leaveApplication.startDate).format('HH:mm:ss')}
									onChange={(e) => {
										const [hours, minutes] = e.target.value.split(':')

										updateLeaveApplication({
											...leaveApplication,
											startDate: moment(leaveApplication.startDate)
												.set({ hour: parseInt(hours, 10), minute: parseInt(minutes, 10), second: 0, millisecond: 0 })
												.toISOString(),
											endDate: moment(leaveApplication.startDate)
												.set({ hour: parseInt(hours, 10), minute: parseInt(minutes, 10), second: 0, millisecond: 0 })
												.add(leaveApplication.leavePeriod![0].numberOfUnits! * 60, 'minutes')
												.toISOString(),
										})
									}}
								/>{' '}
								<p style={{ padding: '7px' }}> To </p>
								<Form.Control type="time" readOnly={true} value={moment(leaveApplication.endDate).format('HH:mm:ss')} />
							</Row>
						) : null}

						<Row>
							<Col style={{ paddingBottom: '15px' }}>
								<Select
									placeholder={'Select Type'}
									isDisabled={!leaveApplication.isNonXero}
									options={
										I.have('admin', props.appState.permission[props.appState.context])
											? props.types!.map((t) => ({
													label: t.name!,
													value: t.xeroOrgLeaveTypeID,
											  }))
											: props
													.types!.filter((t: LeaveType) => t.isEnteredByAdmin === false)
													.map((t) => ({
														label: t.name!,
														value: t.xeroOrgLeaveTypeID,
													}))
									}
									value={
										leaveApplication.leaveType !== undefined
											? {
													label: leaveApplication.leaveType!.name!,
													value: leaveApplication.leaveType!.xeroOrgLeaveTypeID,
											  }
											: undefined
									}
									onChange={(e) => {
										const selected = props.types!.find((t) => t.xeroOrgLeaveTypeID === e!.value)
										updateLeaveApplication({
											...leaveApplication,
											xeroOrgLeaveTypeID: selected!.xeroOrgLeaveTypeID,
											leaveType: selected,
										})
									}}
								/>
							</Col>
						</Row>
						<Row>
							<Col style={{ paddingBottom: '15px' }}>
								<Select
									placeholder={'Select Team Member'}
									isDisabled={!leaveApplication.isNonXero}
									options={props.employees!.map((e: Employee) => ({
										label: `${e!.alias ? e.alias : `${e!.firstName} ${e!.lastName}`}`,
										value: e.employeeID,
									}))}
									value={
										leaveApplication.employee !== undefined
											? {
													label: `${
														leaveApplication.employee!.alias
															? leaveApplication.employee!.alias
															: `${leaveApplication.employee!.firstName} ${leaveApplication.employee!.lastName}`
													}`,
													value: leaveApplication.employeeID,
											  }
											: undefined
									}
									onChange={(e) => {
										const selected = props.employees!.find((t) => t.employeeID === e!.value)
										updateLeaveApplication({
											...leaveApplication,
											employeeID: selected!.employeeID,
											employee: selected,
										})
									}}
								/>
							</Col>
						</Row>
						<Row>
							<Col sm="4">
								<FormLabel>Description (for inclusion in Calendar entries):</FormLabel>
							</Col>
							<Col sm="8">
								<textarea
									name="description"
									disabled={!leaveApplication.isNonXero}
									value={leaveApplication.description}
									className={'form-control'}
									rows={2}
									style={{ marginBottom: '15px' }}
									onChange={(e) =>
										updateLeaveApplication({
											...leaveApplication,
											description: e.target.value,
										})
									}
								/>
							</Col>
						</Row>
						<Row>
							<Col sm="4">
								<FormLabel>Recurring Event:</FormLabel>
							</Col>
							<Col sm="6">
								<label>
									<input
										type="radio"
										value="Yes"
										name="recurrence"
										disabled={!leaveApplication.isNonXero}
										checked={recurring === 'Yes'}
										onChange={() => {
											updateRecurring('Yes')
											updateRecurrenceLeave({
												...convertDayIntoRecurrence(recurrenceLeave, new Date(leaveApplication.startDate!).getDay()),
												startDate: leaveApplication.startDate,
											})
											convertDayIntoRecurrence(recurrenceLeave, new Date(leaveApplication.startDate!).getDay())
										}}
									/>{' '}
									Yes{' '}
								</label>
								<label>
									<input
										type="radio"
										value="No"
										name="recurrence"
										disabled={!leaveApplication.isNonXero}
										checked={recurring === 'No'}
										onChange={() => {
											updateRecurring('No')
											updateRecurrenceLeave(recurrenceEntry)
										}}
										style={{ marginLeft: '15px' }}
									/>{' '}
									No{' '}
								</label>
							</Col>
						</Row>
						{recurring === 'Yes' ? (
							<>
								<Row style={{ marginTop: '15px' }}>
									<Col sm="3">
										<FormLabel>Repeat every </FormLabel>
									</Col>
									<Col sm="3">
										<input
											type="number"
											min="1"
											disabled={!leaveApplication.isNonXero}
											style={{ marginTop: '-8px' }}
											className={'form-control'}
											value={recurrenceLeave.weeklyRegularity}
											onChange={(event) =>
												updateRecurrenceLeave({
													...recurrenceLeave,
													weeklyRegularity: parseInt(event.target.value),
												})
											}
										/>
									</Col>
									<Col sm="4" style={{ marginTop: '-8px' }}>
										<Select
											placeholder={'Select Recurrence Regularity'}
											options={recurrenceRegularity}
											isDisabled={!leaveApplication.isNonXero}
											value={recurrenceRegularity.find(
												(rec) =>
													rec.value ===
													(recurrenceLeave.typeIsWeekly
														? 'week'
														: recurrenceLeave.typeIsAnnually
														? 'year'
														: recurrenceLeave.typeIsMonthly
														? 'month'
														: '')
											)}
											onChange={(e) => {
												switch (e!.value) {
													case 'week':
														updateRecurrenceLeave({
															...recurrenceLeave,
															typeIsWeekly: true,
															typeIsAnnually: false,
															typeIsMonthly: false,
														})
														break
													case 'month':
														updateRecurrenceLeave({
															...recurrenceLeave,
															typeIsWeekly: false,
															typeIsAnnually: false,
															typeIsMonthly: true,
														})
														break
													case 'year':
														updateRecurrenceLeave({
															...recurrenceLeave,
															typeIsWeekly: false,
															typeIsAnnually: true,
															typeIsMonthly: false,
														})
														break
													default:
														return
												}
											}}
										/>
									</Col>
								</Row>
								<Row style={{ marginTop: '15px' }}>
									<Col sm="3">
										<FormLabel>Repeat on </FormLabel>
									</Col>
								</Row>
								<Row style={{ marginLeft: '0px', paddingBottom: '10px' }}>
									<button
										disabled={recurrenceLeave.typeIsAnnually || recurrenceLeave.typeIsMonthly}
										type="button"
										className={recurrenceLeave.isMonday ? 'focus-button-weekday-picker' : 'button-weekday-picker'}
										title="Monday"
										onClick={() =>
											updateRecurrenceLeave({
												...recurrenceLeave,
												isMonday: !recurrenceLeave.isMonday,
											})
										}
									>
										<span>M</span>
									</button>
									<button
										disabled={recurrenceLeave.typeIsAnnually || recurrenceLeave.typeIsMonthly}
										type="button"
										className={recurrenceLeave.isTuesday ? 'focus-button-weekday-picker' : 'button-weekday-picker'}
										title="Tuesday"
										onClick={() =>
											updateRecurrenceLeave({
												...recurrenceLeave,
												isTuesday: !recurrenceLeave.isTuesday,
											})
										}
									>
										<span>T</span>
									</button>
									<button
										disabled={recurrenceLeave.typeIsAnnually || recurrenceLeave.typeIsMonthly}
										type="button"
										className={recurrenceLeave.isWednesday ? 'focus-button-weekday-picker' : 'button-weekday-picker'}
										title="Wednesday"
										onClick={() =>
											updateRecurrenceLeave({
												...recurrenceLeave,
												isWednesday: !recurrenceLeave.isWednesday,
											})
										}
									>
										<span>W</span>
									</button>
									<button
										disabled={recurrenceLeave.typeIsAnnually || recurrenceLeave.typeIsMonthly}
										type="button"
										className={recurrenceLeave.isThursday ? 'focus-button-weekday-picker' : 'button-weekday-picker'}
										title="Thursday"
										onClick={() =>
											updateRecurrenceLeave({
												...recurrenceLeave,
												isThursday: !recurrenceLeave.isThursday,
											})
										}
									>
										<span>T</span>
									</button>
									<button
										disabled={recurrenceLeave.typeIsAnnually || recurrenceLeave.typeIsMonthly}
										type="button"
										className={recurrenceLeave.isFriday ? 'focus-button-weekday-picker' : 'button-weekday-picker'}
										title="Friday"
										onClick={() =>
											updateRecurrenceLeave({
												...recurrenceLeave,
												isFriday: !recurrenceLeave.isFriday,
											})
										}
									>
										<span>F</span>
									</button>
									<button
										disabled={recurrenceLeave.typeIsAnnually || recurrenceLeave.typeIsMonthly}
										type="button"
										className={recurrenceLeave.isSaturday ? 'focus-button-weekday-picker' : 'button-weekday-picker'}
										title="Saturday"
										onClick={() =>
											updateRecurrenceLeave({
												...recurrenceLeave,
												isSaturday: !recurrenceLeave.isSaturday,
											})
										}
									>
										<span>S</span>
									</button>
									<button
										disabled={recurrenceLeave.typeIsAnnually || recurrenceLeave.typeIsMonthly}
										type="button"
										className={recurrenceLeave.isSunday ? 'focus-button-weekday-picker' : 'button-weekday-picker'}
										title="Sunday"
										onClick={() =>
											updateRecurrenceLeave({
												...recurrenceLeave,
												isSunday: !recurrenceLeave.isSunday,
											})
										}
									>
										<span>S</span>
									</button>
								</Row>
								<Row>
									<Col sm="3">
										<FormLabel>Ends</FormLabel>
									</Col>
								</Row>
								<Row>
									<Col sm="3">
										<label>
											<input
												type="radio"
												value="Never"
												name="endingRecurrence"
												disabled={!leaveApplication.isNonXero}
												checked={endingRecurrence === 'Never'}
												onChange={() => {
													updateEndingRecurrence('Never')
													updateRecurrenceLeave({
														...recurrenceLeave,
														endDate: null,
													})
												}}
											/>{' '}
											Never{' '}
										</label>
									</Col>
								</Row>
								<Row style={{ marginTop: '15px' }}>
									<Col sm="2">
										<label>
											<input
												type="radio"
												value="On"
												name="endingRecurrence"
												disabled={!leaveApplication.isNonXero}
												checked={endingRecurrence === 'On'}
												onChange={() => updateEndingRecurrence('On')}
											/>{' '}
											On{' '}
										</label>
									</Col>
									<Col sm="6" style={{ marginTop: '-6px' }}>
										<Form.Control
											type="date"
											name="endDate"
											readOnly={endingRecurrence !== 'On'}
											value={endingRecurrence === 'On' ? moment.utc(recurrenceLeave.endDate!).local().format('YYYY-MM-DD') : undefined}
											onChange={(e) => {
												const localDate = moment.utc(e.target.value, 'YYYY-MM-DD').startOf('day').toISOString()
												updateRecurrenceLeave({
													...recurrenceLeave,
													endDate: localDate,
												})
											}}
										/>
									</Col>
								</Row>
								<Row style={{ marginTop: '15px', marginBottom: '20px' }}>
									<Col sm="2">
										<label>
											<input
												type="radio"
												value="After"
												disabled={!leaveApplication.isNonXero}
												name="endingRecurrence"
												checked={endingRecurrence === 'After'}
												onChange={() => updateEndingRecurrence('After')}
											/>{' '}
											After{' '}
										</label>
									</Col>
									<Col sm="3">
										<input
											type="number"
											min="0"
											readOnly={endingRecurrence !== 'After'}
											value={occurrences!}
											style={{ marginTop: '-8px' }}
											className={'form-control'}
											disabled={!leaveApplication.isNonXero}
											onChange={(e) => {
												updateOccurrences(parseInt(e.target.value))
												updateRecurrenceLeave({
													...convertDayIntoRecurrence(recurrenceLeave, new Date(leaveApplication.startDate!).getDay()),
													endDate: calculateEndDate(recurrenceLeave, recurring, endingRecurrence, parseInt(e.target.value)),
												})
											}}
										/>
									</Col>
									<Col sm="3">
										<FormLabel>occurrence(s)</FormLabel>
									</Col>
								</Row>
							</>
						) : null}

						{!isEdit ? (
							<Button
								variant={'success'}
								style={{ marginRight: '15px' }}
								disabled={loading}
								onClick={() => {
									handleCalendarEntryCreation()
								}}
							>
								Save
							</Button>
						) : (
							<Button
								variant={'success'}
								style={{ marginRight: '15px' }}
								disabled={loading}
								onClick={() => {
									updateLeaveApplication({
										...leaveApplication,
										recurrence: recurrenceLeave,
									})
									handleUpdatePress()
								}}
							>
								Update
							</Button>
						)}

						<Button
							variant={'secondary'}
							disabled={loading}
							onClick={() => {
								resetModal()
								props.handleClose()
							}}
						>
							Cancel
						</Button>
					</FormGroup>
				</Modal.Body>
			</Modal>
			{showEditRecurrenceDialog && (
				<HandleEventPopUp
					leaveApplication={leaveApplication}
					leaveApplicationID={leaveApplication.leaveApplicationID!}
					popUpEventType={'Edit'}
					handleClose={() => {
						updateShowEditRecurrenceDialog(false)
						updateShowDialog(false)
						resetModal()
						props.handleClose()
					}}
					show={showEditRecurrenceDialog}
					leaveApplications={props.leaveApplications}
					updateMessages={props.updateMessages}
					appState={props.appState}
					updateLeaveApplications={props.updateLeaveApplications}
					calendar={props.currentCalendar}
					xeroOrgCalendars={props.xeroOrgCalendars}
				/>
			)}
		</>
	)
}

const checkHoursWithinPeriod = (start: string | number | Date, end: string | number | Date) => {
	return (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 3600) + 24 //+24 to include the end date hours
}

const calculateEndDate = (recurrenceLeave: Recurrence, recurring: string, endingRecurrence: EndingRecurrences, occurrences: number) => {
	const days = [
		recurrenceLeave.isSunday,
		recurrenceLeave.isMonday,
		recurrenceLeave.isTuesday,
		recurrenceLeave.isWednesday,
		recurrenceLeave.isThursday,
		recurrenceLeave.isFriday,
		recurrenceLeave.isSaturday,
	]
	if (recurring === 'Yes' && endingRecurrence === 'After' && recurrenceLeave.weeklyRegularity && days.includes(true)) {
		// Monthly or Yearly recurrence
		if (recurrenceLeave.typeIsMonthly || recurrenceLeave.typeIsAnnually) {
			const lastStart = moment.utc(recurrenceLeave.startDate).startOf('day')
			return moment
				.utc(lastStart)
				.add((occurrences - 1) * recurrenceLeave.weeklyRegularity, recurrenceLeave.typeIsMonthly ? 'month' : 'year')
				.format('YYYY-MM-DD')
		} else {
			// Weekly recurrence across selected weekdays
			const startDate = moment.utc(recurrenceLeave.startDate).startOf('day')
			let currentDate = startDate.clone()
			let count = 0
			let weeksPassed = 0

			while (count < occurrences) {
				for (let i = 0; i < 7 && count < occurrences; i++) {
					const dayOfWeek = currentDate.day()
					if (days[dayOfWeek]) {
						count++
						if (count === occurrences) {
							break
						}
					}
					currentDate.add(1, 'day')
				}

				if (count < occurrences) {
					weeksPassed++
					currentDate = startDate.clone().add(weeksPassed * recurrenceLeave.weeklyRegularity, 'weeks')
				}
			}

			return currentDate.format('YYYY-MM-DD')
		}
	}

	return null
}

const convertDayIntoRecurrence = (recurrenceLeave: Recurrence, day: number) => {
	switch (day) {
		case 0:
			return { ...recurrenceLeave, isSunday: true }
		case 1:
			return { ...recurrenceLeave, isMonday: true }
		case 2:
			return { ...recurrenceLeave, isTuesday: true }
		case 3:
			return { ...recurrenceLeave, isWednesday: true }
		case 4:
			return { ...recurrenceLeave, isThursday: true }
		case 5:
			return { ...recurrenceLeave, isFriday: true }
		case 6:
			return { ...recurrenceLeave, isSaturday: true }
		default:
			return { ...recurrenceLeave, isMonday: true }
	}
}

const getSchema = (endingRecurrence: EndingRecurrences) =>
	yup.object().shape({
		description: yup.string().optional(),
		startDate: yup.date().required('Start date is required.'),
		endDate: yup.date().min(yup.ref('startDate'), 'End date cannot be before start date.').required('End date is required.'),
		employeeID: yup.string().required('Employee is required.'),
		xeroOrgLeaveTypeID: yup.string().required('Leave type is required.'),
		leavePeriod: yup.array().of(
			yup.object().shape({
				numberOfUnits: yup
					.number()
					.min(0, 'Number of hours cannot be below 0.')
					.test({
						name: 'max',
						exclusive: false,
						params: {},
						message: 'Number of hours cannot be greater than\n the total hours in a period.',
						test: (value, context) => value! <= checkHoursWithinPeriod(context.parent.startDate, context.parent.endDate),
					})
					.required('Number of hours is required.'),
			})
		),
		recurrence: yup
			.object()
			.shape({
				weeklyRegularity: yup.number().min(1).required('Regularity frequency is required'),
				startDate: yup.date().required('Start date is required'),
				endDate:
					endingRecurrence === 'On'
						? yup.date().required('Ends on date is required').nullable()
						: endingRecurrence === 'After'
						? yup.date().required('Ends after occurences is required').nullable()
						: yup.date().optional().nullable(),
				isMonday: yup.boolean(),
				isTuesday: yup.boolean(),
				isWednesday: yup.boolean(),
				isThursday: yup.boolean(),
				isFriday: yup.boolean(),
				isSaturday: yup.boolean(),
				isSunday: yup.boolean(),
				daySelection: yup.boolean().when(['isMonday', 'isTuesday', 'isWednesday', 'isThursday', 'isFriday', 'isSaturday', 'isSunday'], {
					is: (
						isMonday: boolean,
						isTuesday: boolean,
						isWednesday: boolean,
						isThursday: boolean,
						isFriday: boolean,
						isSaturday: boolean,
						isSunday: boolean
					) => !isMonday && !isTuesday && !isWednesday && !isThursday && !isFriday && !isSaturday && !isSunday,
					then: yup.boolean().required('At least one repeating day required.'),
				}),
				typeIsWeekly: yup.boolean(),
				typeIsMonthly: yup.boolean(),
				typeIsAnnually: yup.boolean(),
			})
			.notRequired()
			.default(undefined),
	})
